/**
 * `136:950`'s right rail, "Quem usa este perfil": the first `RAIL_WINDOW`
 * accounts this profile resolves to (handle plus campaign, matching the
 * frame), a count badge, and an overflow link opening the full sortable,
 * independently-paged table (`AccountsModal`). The backing route is real,
 * not `ASSUMED`: see `AccountsModal.tsx`'s own `useProfileAccountsPage`.
 */

import { useId, useRef, useState, type ReactNode } from 'react';

import { failureSentence } from '../../../../chrome/state';
import { Badge } from '../../../../ui/badge';
import { Skeleton } from '../../../../ui/skeleton';
import { profileCatalogue } from './messages';
import { AccountsModal, useProfileAccountsPage } from './AccountsModal';

// Matches the frame: four rows draw on their own, the rest fold into the
// overflow link -- same window `ReplanConfirmation.tsx`'s own `DRAWN_WINDOW`
// uses for the same rail shape.
const RAIL_WINDOW = 4;

const at = profileCatalogue.t;
const atPlural = profileCatalogue.plural;

export function AccountsRail(props: { tenantId: string; profileId: string }): ReactNode {
  const titleId = useId();
  const [modalOpen, setModalOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const railQuery = useProfileAccountsPage(props.tenantId, props.profileId, 1, RAIL_WINDOW);
  const failure = railQuery.error?.failure;
  const overflow = railQuery.data !== undefined ? railQuery.data.total - RAIL_WINDOW : 0;

  // Inlined rather than a nested component: a component type declared inside
  // another component's render is a fresh type every render, so React
  // remounts its whole subtree (and drops any focus inside it) the instant a
  // sibling state change -- opening this very modal -- re-renders the rail.
  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h2 id={titleId} className="text-title-sm">
          {at('accounts.title')}
        </h2>
        {railQuery.data !== undefined && (
          <Badge variant="secondary">
            {atPlural('accounts.count.other', railQuery.data.total, {
              count: railQuery.data.total,
            })}
          </Badge>
        )}
      </div>
      <div>
        {failure !== undefined ? (
          <p role="alert" className="flex items-start gap-2 text-caption text-destructive">
            {failureSentence(failure)}
          </p>
        ) : railQuery.data === undefined ? (
          <div data-testid="accounts-rail-loading" className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : railQuery.data.total === 0 ? (
          <p data-testid="accounts-rail-empty" className="text-caption text-muted-foreground">
            {at('accounts.empty')}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <ul className="flex flex-col divide-y">
              {railQuery.data.items.map((account) => (
                <li key={account.id} className="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0">
                  <span className="text-body-sm font-medium">
                    {account.handle ?? at('accounts.handleNone')}
                  </span>
                  <span className="text-caption text-muted-foreground">
                    {account.campaign_name}
                  </span>
                </li>
              ))}
            </ul>
            {overflow > 0 && (
              <button
                ref={triggerRef}
                type="button"
                className="self-start text-caption text-primary-ink underline-offset-4 hover:underline focus-visible:underline"
                onClick={() => setModalOpen(true)}
              >
                {atPlural('accounts.overflow.other', overflow, { count: overflow })}
              </button>
            )}
          </div>
        )}
      </div>
      <AccountsModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        tenantId={props.tenantId}
        profileId={props.profileId}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          triggerRef.current?.focus();
        }}
      />
    </section>
  );
}
